import JSEncrypt from 'jsencrypt';
import CryptoJS from 'crypto-js';

// RSA密钥对生成
export const generateKeyPair = () => {
  const encrypt = new JSEncrypt({default_key_size: 2048});
  const publicKey = encrypt.getPublicKey();
  const privateKey = encrypt.getPrivateKey();
  
  return {
    publicKey,
    privateKey,
    userId: generateUserIdFromPublicKey(publicKey)
  };
};

// 从公钥生成用户ID（MD5前12位）
export const generateUserIdFromPublicKey = (publicKey) => {
  const hash = CryptoJS.MD5(publicKey).toString();
  return hash.substring(0, 12);
};

// 生成随机密钥
export const generateRandomKey = (length = 32) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// RSA加密
export const rsaEncrypt = (data, publicKey) => {
  try {
    const encrypt = new JSEncrypt();
    encrypt.setPublicKey(publicKey);
    return encrypt.encrypt(data);
  } catch (error) {
    return null;
  }
};

// RSA解密
export const rsaDecrypt = (encryptedData, privateKey) => {
  try {
    const encrypt = new JSEncrypt();
    encrypt.setPrivateKey(privateKey);
    return encrypt.decrypt(encryptedData);
  } catch (error) {
    return null;
  }
};

// AES加密 - AES-ECB-PKCS7-256
export const aesEncrypt = (data, password) => {
  try {
    
    // 确保密码是256位（32字节）
    const key = CryptoJS.enc.Utf8.parse(password.padEnd(32, '\0').substring(0, 32));
    
    const encrypted = CryptoJS.AES.encrypt(data, key, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    });
    
    const result = encrypted.toString();
    return result;
  } catch (error) {
    return null;
  }
};

// AES解密 - AES-ECB-PKCS7-256
export const aesDecrypt = (encryptedData, password) => {
  try {
    
    // 确保密码是256位（32字节）
    const key = CryptoJS.enc.Utf8.parse(password.padEnd(32, '\0').substring(0, 32));
    
    const bytes = CryptoJS.AES.decrypt(encryptedData, key, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    });
    
    const result = bytes.toString(CryptoJS.enc.Utf8);
    
    // 检查解密结果是否有效
    if (!result || result.length === 0) {
      return null;
    }
    
    return result;
  } catch (error) {
    return null;
  }
};

// 保存密钥到本地存储
export const saveKeysToStorage = (keys) => {
  localStorage.setItem('toxchat_keys', JSON.stringify(keys));
};

// 从本地存储加载密钥
export const loadKeysFromStorage = () => {
  try {
    const stored = localStorage.getItem('toxchat_keys');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
};

// 导出密钥文件
export const exportKeysToFile = (keys) => {
  const keyData = {
    publicKey: keys.publicKey,
    privateKey: keys.privateKey,
    userId: keys.userId,
    exportTime: new Date().toISOString()
  };
  
  const blob = new Blob([JSON.stringify(keyData, null, 2)], {
    type: 'application/json'
  });
  
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `toxchat_keys_${keys.userId}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

// 从文件导入密钥
export const importKeysFromFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const keyData = JSON.parse(e.target.result);
        if (keyData.publicKey && keyData.privateKey) {
          resolve({
            publicKey: keyData.publicKey,
            privateKey: keyData.privateKey,
            userId: keyData.userId || generateUserIdFromPublicKey(keyData.publicKey)
          });
        } else {
          reject(new Error('无效的密钥文件格式'));
        }
      } catch (error) {
        reject(new Error('密钥文件解析失败'));
      }
    };
    reader.readAsText(file);
  });
};

// 笔记专用加密 - 双重加密
export const encryptNote = (content, userPublicKey, serverPublicKey) => {
  try {
    // 第一步：使用用户公钥加密
    const userEncrypted = rsaEncrypt(content, userPublicKey);
    if (!userEncrypted) {
      throw new Error('用户公钥加密失败');
    }
    
    // 第二步：使用服务器公钥加密
    const serverEncrypted = rsaEncrypt(userEncrypted, serverPublicKey);
    if (!serverEncrypted) {
      throw new Error('服务器公钥加密失败');
    }
    
    return serverEncrypted;
  } catch (error) {
    return null;
  }
};

// 笔记专用解密 - 双重解密
export const decryptNote = (encryptedContent, userPrivateKey, serverPrivateKey) => {
  try {
    // 第一步：使用服务器私钥解密
    const serverDecrypted = rsaDecrypt(encryptedContent, serverPrivateKey);
    if (!serverDecrypted) {
      throw new Error('服务器私钥解密失败');
    }
    
    // 第二步：使用用户私钥解密
    const userDecrypted = rsaDecrypt(serverDecrypted, userPrivateKey);
    if (!userDecrypted) {
      throw new Error('用户私钥解密失败');
    }
    
    return userDecrypted;
  } catch (error) {
    return null;
  }
};

// RC4加密（使用AES-CFB模拟）
export const rc4Encrypt = (data, key) => {
  try {
    // 使用AES-CFB模式模拟RC4
    const keyWordArray = CryptoJS.enc.Utf8.parse(key);
    const iv = CryptoJS.lib.WordArray.random(16);
    
    const encrypted = CryptoJS.AES.encrypt(data, keyWordArray, {
      iv: iv,
      mode: CryptoJS.mode.CFB,
      padding: CryptoJS.pad.NoPadding
    });
    
    // 将IV和加密数据组合
    const result = iv.concat(encrypted.ciphertext);
    return CryptoJS.enc.Base64.stringify(result);
  } catch (error) {
    return null;
  }
};

// RC4解密（使用AES-CFB模拟）
export const rc4Decrypt = (encryptedData, key) => {
  try {
    // 解析Base64数据
    const encryptedWordArray = CryptoJS.enc.Base64.parse(encryptedData);
    
    // 提取IV（前16字节）
    const iv = CryptoJS.lib.WordArray.create(encryptedWordArray.words.slice(0, 4));
    const ciphertext = CryptoJS.lib.WordArray.create(encryptedWordArray.words.slice(4));
    
    const keyWordArray = CryptoJS.enc.Utf8.parse(key);
    
    const decrypted = CryptoJS.AES.decrypt(
      CryptoJS.lib.CipherParams.create({ ciphertext: ciphertext }),
      keyWordArray,
      {
        iv: iv,
        mode: CryptoJS.mode.CFB,
        padding: CryptoJS.pad.NoPadding
      }
    );
    
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    return null;
  }
};

// 生成服务器公钥的MD5值
export const getServerPublicKeyMD5 = (serverPublicKey) => {
  return CryptoJS.MD5(serverPublicKey).toString();
};

// 生成用户密钥
export const generateUserSecretKey = () => {
  return CryptoJS.lib.WordArray.random(32).toString(CryptoJS.enc.Base64);
};

// 生成挑战
export const generateChallenge = () => {
  return CryptoJS.lib.WordArray.random(32).toString(CryptoJS.enc.Base64);
};

// 生成Nonce
export const generateNonce = () => {
  return CryptoJS.lib.WordArray.random(16).toString(CryptoJS.enc.Hex);
};

// HMAC-SHA256签名
export const hmacSha256 = (data, key) => {
  return CryptoJS.HmacSHA256(data, key).toString();
};

// 生成动态签名
export const generateDynamicSignature = (jwtToken, timestamp, nonce, requestData, encryptedUserSecretKey, userPrivateKey) => {
  // 先解密用户密钥
  const userSecretKey = rsaDecrypt(encryptedUserSecretKey, userPrivateKey);
  
  if (!userSecretKey) {
    throw new Error('解密用户密钥失败');
  }
  
  // 确保JSON序列化按字典序排序，与后端保持一致
  const sortedRequestData = sortObjectKeys(requestData);
  const requestDataJSON = JSON.stringify(sortedRequestData);
  
  const signatureData = `${jwtToken}.${timestamp}.${nonce}.${requestDataJSON}`;
  
  const signature = hmacSha256(signatureData, userSecretKey);
  
  return signature;
};

// 递归排序对象键名，确保与Go的json.Marshal行为一致
const sortObjectKeys = (obj) => {
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }
  
  const sortedKeys = Object.keys(obj).sort();
  const sortedObj = {};
  
  for (const key of sortedKeys) {
    sortedObj[key] = sortObjectKeys(obj[key]);
  }
  
  return sortedObj;
};
